import { useStatsigClient } from "@statsig/react-bindings";
import { useCallback } from "react";

// Development mode detection
const isDevelopment = import.meta.env.DEV;

// Custom hook for analytics
export function useAnalytics() {
	const statsigClient = useStatsigClient();

	const logEvent = useCallback(
		(
			eventName: string,
			value?: string | number,
			metadata?: Record<string, unknown>,
		) => {
			// Log all events in development for debugging visibility
			if (isDevelopment) {
				const valueStr = value !== undefined ? ` (${value})` : "";
				const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : "";
				console.log(`[Statsig] ${eventName}${valueStr}${metaStr}`);
			}

			if (!statsigClient) {
				return;
			}

			try {
				// Convert metadata to Record<string, string> as required by Statsig
				const stringMetadata = metadata
					? Object.entries(metadata).reduce(
							(acc, [key, val]) => {
								acc[key] = String(val);
								return acc;
							},
							{} as Record<string, string>,
						)
					: undefined;

				statsigClient.logEvent(eventName, value, stringMetadata);
			} catch (error) {
				console.error("[Statsig] Error logging event:", error);
			}
		},
		[statsigClient],
	);

	const logPageView = useCallback(
		(page: string) => {
			logEvent("page_view", page, {
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	const logUserAction = useCallback(
		(action: string, metadata?: Record<string, unknown>) => {
			logEvent("user_action", action, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	const logAuth = useCallback(
		(
			action:
				| "login"
				| "logout"
				| "login_failed"
				| "user_signed_up"
				| "user_logged_in",
			metadata?: Record<string, unknown>,
		) => {
			logEvent("auth_event", action, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	const logError = useCallback(
		(error: string, metadata?: Record<string, unknown>) => {
			logEvent("error_event", error, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	return {
		logEvent,
		logPageView,
		logUserAction,
		logAuth,
		logError,
	};
}

// Export the Analytics type for use in other services
export type Analytics = ReturnType<typeof useAnalytics>;

// Standalone analytics functions for use outside React components
export const analytics = {
	logEvent: (
		eventName: string,
		value?: string | number,
		metadata?: Record<string, unknown>,
	) => {
		// Log to console in development for debugging visibility
		if (isDevelopment) {
			const valueStr = value !== undefined ? ` (${value})` : "";
			const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : "";
			console.log(`[Statsig] ${eventName}${valueStr}${metaStr}`);
		}
		// In production, this would need to be handled differently or use a global Statsig instance
	},
};
